import { Box, Paper } from '@mui/material';
import {
  add,
  differenceInDays,
  getDay,
  getMonth,
  getYear,
  set,
} from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useResizeDetector } from 'react-resize-detector';
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Label,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getDayOfWeek, getMonthText, getTypeWord } from '../../common/helper';
import NoDataPage from '../commonComponent/NoDataPage';
import { SwTitleDownloadMore } from '../commonComponent/SwTitleDownloadMore';
import { SwRwwTab } from '../commonComponent/SwRwwTab';
import { RwwCumulativeGraphByGender } from './RwwCumulativeGraphByGender';
import {
  KEY_CUMULATIVE,
  KEY_PORTAL_MONTHLY_READ_MEDAL_LIST,
  KEY_PORTAL_MONTHLY_WALK_MEDAL_LIST,
  KEY_PORTAL_MONTHLY_WRITE_MEDAL_LIST,
  KEY_READ_TEXT,
  KEY_WALK_TEXT,
  KEY_WRITE_TEXT,
} from '../../common/key';
import {
  downloadPortalMonthlyDataList,
  downloadPortalMonthlyMedalDataList,
  getPortalMonthlyReadMedalList,
  getPortalMonthlyWalkMedalList,
  getPortalMonthlyWriteMedalList,
} from '../../common/api/ApiPortal';
import { RwwGraphByMedal } from './RwwGraphByMedal';

interface Props {
  title: string;
  startDate: number;
  from?: string;
  isPdf?: boolean;
}

export const RwwMedalStatistics: React.FC<Props> = ({
  title,
  startDate,
  from = 'dashboard',
  isPdf = false,
}) => {
  const [reqRequest, setReqRequest] = useState<boolean>(false);
  const [requestData, setRequestData] = useState<any>();
  const [requestReadData, setRequestReadData] = useState<any>();
  const [requestWalkData, setRequestWalkData] = useState<any>();
  const [requestWriteData, setRequestWriteData] = useState<any>();
  const [rowCount, setRowCount] = useState<number>(1);
  const [rateData, setRateData] = useState<any>([]);
  const { width, ref } = useResizeDetector();
  const [loading, setLoading] = useState<any>(false);
  const [statType, setStatType] = useState<string>(KEY_READ_TEXT);

  const {
    data,
    isLoading,
    refetch: refetchPortalMonthlyReadMedal,
  } = useQuery(
    KEY_PORTAL_MONTHLY_READ_MEDAL_LIST,
    () => {
      if (reqRequest) {
        const newData: any = {
          year: getYear(startDate * 1000),
          month: getMonthText(startDate * 1000),
        };
        return getPortalMonthlyReadMedalList(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null) {
          setRequestData(res);
        } else {
          setRequestData(null);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const { refetch: refetchPortalMonthlyWalkMedal } = useQuery(
    KEY_PORTAL_MONTHLY_WALK_MEDAL_LIST,
    () => {
      if (reqRequest) {
        const newData: any = {
          year: getYear(startDate * 1000),
          month: getMonthText(startDate * 1000),
        };
        return getPortalMonthlyWalkMedalList(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null) {
          setRequestData(res);
        } else {
          setRequestData(null);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const { refetch: refetchPortalMonthlyWriteMedal } = useQuery(
    KEY_PORTAL_MONTHLY_WRITE_MEDAL_LIST,
    () => {
      if (reqRequest) {
        const newData: any = {
          year: getYear(startDate * 1000),
          month: getMonthText(startDate * 1000),
        };
        return getPortalMonthlyWriteMedalList(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null) {
          setRequestData(res);
        } else {
          setRequestData(null);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const downloadPortalMonthlyMedalData = () => {
    const newData: any = {
      type: getTypeWord(statType),
      dataType: KEY_CUMULATIVE,
      year: getYear(startDate * 1000),
      month: getMonthText(startDate * 1000),
    };
    downloadPortalMonthlyMedalDataList(newData);
  };

  useEffect(() => {
    if (reqRequest) {
      refetchPortalMonthlyReadMedal();
    }
  }, [startDate]);

  useEffect(() => {
    if (reqRequest) {
      refetchPortalMonthlyReadMedal();
    }
  }, [reqRequest]);

  useEffect(() => {
    setReqRequest(true);
  }, []);

  return (
    <>
      <Paper>
        <SwTitleDownloadMore title={title} />
        <SwRwwTab
          onChangeTab={value => {
            setStatType(
              value === 0
                ? KEY_READ_TEXT
                : value === 1
                ? KEY_WALK_TEXT
                : KEY_WRITE_TEXT
            );
            value === 0
              ? refetchPortalMonthlyReadMedal()
              : value === 1
              ? refetchPortalMonthlyWalkMedal()
              : refetchPortalMonthlyWriteMedal();
          }}
          onDownload={() => downloadPortalMonthlyMedalData()}
        />
        <RwwGraphByMedal
          stepsGoalRateAverage={[false, false]}
          startDate={startDate}
          totalAverage={requestData?.totalRate ?? 0}
          data={requestData?.rate}
          isPdf={false}
        />
      </Paper>
    </>
  );
};
